import { CaretRightIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from './cn';

export interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
  className?: string;
}

export function PageHeader({ title, subtitle, actions, back, className }: PageHeaderProps) {
  return (
    <header className={cn('flex items-start justify-between gap-3 pt-2 pb-5 lg:pt-8', className)}>
      <div className="min-w-0">
        {back}
        <h1 className="t-display text-[1.75rem] text-ink lg:text-4xl">{title}</h1>
        {subtitle ? <div className="mt-1.5 text-[0.9375rem] text-ink-2">{subtitle}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
    </header>
  );
}

export function Section({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('mb-8', className)}>
      <div className="mb-2.5 flex items-center justify-between gap-3 px-1">
        <h2 className="t-section">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Gruppo di righe su superficie bianca, separate da linee sottili (niente "kit di card"). */
export function ListGroup({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <ul
      className={cn(
        'divide-y divide-line-2 overflow-hidden rounded-[var(--radius-sheet)] bg-sheet',
        className,
      )}
    >
      {children}
    </ul>
  );
}

export interface ListRowProps {
  title: ReactNode;
  detail?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  to?: string;
  onClick?: () => void;
  className?: string;
  /** Mostra la freccia a destra quando la riga apre qualcosa. */
  chevron?: boolean;
  /** Il titolo va a capo invece di essere troncato. */
  wrap?: boolean;
}

export function ListRow({
  title,
  detail,
  leading,
  trailing,
  to,
  onClick,
  className,
  chevron,
  wrap,
}: ListRowProps) {
  const content = (
    <>
      {leading ? <span className="flex shrink-0 items-center">{leading}</span> : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            'text-[0.9375rem] font-medium text-ink',
            wrap ? 'leading-snug' : 'truncate',
          )}
        >
          {title}
        </span>
        {detail ? <span className="truncate text-sm text-ink-2">{detail}</span> : null}
      </span>
      {trailing ? <span className="flex shrink-0 items-center gap-2">{trailing}</span> : null}
      {chevron ? <CaretRightIcon size={16} className="shrink-0 text-ink-3" aria-hidden /> : null}
    </>
  );
  const rowClass = cn('flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left', className);
  if (to) {
    return (
      <li>
        <Link
          to={to}
          className={cn(rowClass, 'transition-colors hover:bg-sheet-2 active:bg-sheet-2')}
        >
          {content}
        </Link>
      </li>
    );
  }
  if (onClick) {
    return (
      <li>
        <button
          type="button"
          onClick={onClick}
          className={cn(rowClass, 'transition-colors hover:bg-sheet-2 active:bg-sheet-2')}
        >
          {content}
        </button>
      </li>
    );
  }
  return <li className={rowClass}>{content}</li>;
}
